import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import {
  Loader2, CheckCircle, XCircle, HelpCircle, AlertCircle, CircleDashed, Bell,
} from 'lucide-react';

/**
 * Select Playing Team dialog (iter279 extraction).
 *
 * Lets the admin pick the playing roster from RSVP responses, with
 * separate buckets for Confirmed (yes), Not Responded, Tentative (maybe),
 * and Unavailable (no). Also exposes the cross-bucket "X selected /
 * X now unavailable" reconciliation pill that surfaces a user-reported
 * mismatch bug between selected-count and confirmed-count.
 */
const SelectPlayersDialog = ({
  open,
  onOpenChange,
  selectedGame,
  rsvpsWithCounts,
  selectedPlayerIds,
  setSelectedPlayerIds,
  togglePlayerSelection,
  selectAllYesRsvps,
  clearAllSelections,
  onSaveSelectedPlayers,
  onSendRsvpReminder,
  submitting,
  sendingNudge,
}) => {
  // ----- Header reconciliation logic -----
  // Count how many of the currently-selected players are in the
  // Unavailable bucket — that's the source of the "12 selected vs 11
  // confirmed" mismatch admins were seeing. Surface it explicitly so
  // the totals always reconcile.
  const unavailableSelected = rsvpsWithCounts
    .filter(r => r.response === 'no' && selectedPlayerIds.includes(r.player_id))
    .length;
  // The "X selected" pill should reflect the *effective* attending
  // count (i.e. players we'll actually play). Players who RSVP'd
  // Unavailable AFTER being selected stay in `selectedPlayerIds`
  // until the admin clicks "Remove Unavailable".
  const effectiveSelected = selectedPlayerIds.length - unavailableSelected;
  const removeUnavailableFromSelection = () => {
    const unavailableIds = new Set(
      rsvpsWithCounts.filter(r => r.response === 'no').map(r => r.player_id)
    );
    setSelectedPlayerIds(prev => prev.filter(id => !unavailableIds.has(id)));
  };

  // Footer reminder-button calculation
  const nudgeableIds = new Set(
    rsvpsWithCounts
      .filter((r) => r.response === 'not_responded' || r.response === 'tentative')
      .map((r) => r.player_id)
  );
  const selectedNudgeableCount = selectedPlayerIds.filter((pid) => nudgeableIds.has(pid)).length;

  // Pre-filtered bucket arrays
  const yesRsvps = rsvpsWithCounts.filter(r => r.response === 'yes');
  const notRespondedRsvps = rsvpsWithCounts.filter(r => r.response === 'not_responded');
  const tentativeRsvps = rsvpsWithCounts.filter(r => r.response === 'tentative');
  const noRsvps = rsvpsWithCounts.filter(r => r.response === 'no');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select Playing Team - vs {selectedGame?.opponent_team}</DialogTitle>
          <DialogDescription>
            {selectedGame?.game_date} at {selectedGame?.start_time} • {selectedGame?.age_category?.toUpperCase()}
            <br />
            Select players who will play in this game. Game counts show how many games each player has been selected for in this tournament.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-sm" data-testid="select-players-total-count">
                {effectiveSelected} selected
              </Badge>
              {unavailableSelected > 0 && (
                <Badge
                  className="bg-amber-100 text-amber-800 border-amber-300 text-xs flex items-center gap-1"
                  data-testid="select-players-unavailable-warning"
                  title="These players have RSVP'd Unavailable but were previously selected. Their game count still increments unless you remove them."
                >
                  <AlertCircle className="h-3 w-3" />
                  {unavailableSelected} now unavailable
                </Badge>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllYesRsvps}
                data-testid="select-all-yes-btn"
              >
                Select All "Yes"
              </Button>
              {unavailableSelected > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={removeUnavailableFromSelection}
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="remove-unavailable-from-selection-btn"
                >
                  Remove Unavailable ({unavailableSelected})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllSelections}
                data-testid="clear-all-selections-btn"
              >
                Clear All
              </Button>
            </div>
          </div>

          {rsvpsWithCounts.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No RSVPs yet</p>
          ) : (
            <div className="space-y-2">
              {/* Yes RSVPs first */}
              {yesRsvps.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Confirmed ({yesRsvps.length})
                  </h4>
                  <div className="space-y-1">
                    {yesRsvps.map(rsvp => (
                      <div
                        key={rsvp.player_id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedPlayerIds.includes(rsvp.player_id)
                            ? 'bg-green-50 border-green-300'
                            : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}
                        onClick={() => togglePlayerSelection(rsvp.player_id)}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedPlayerIds.includes(rsvp.player_id)}
                            onCheckedChange={() => togglePlayerSelection(rsvp.player_id)}
                            data-testid={`select-player-${rsvp.player_id}`}
                          />
                          <div>
                            <p className="font-medium">{rsvp.player_name}</p>
                            <p className="text-xs text-slate-500">
                              Responded by: {rsvp.responded_by_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="secondary"
                            className={`${
                              rsvp.games_played_in_tournament === 0
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            {rsvp.games_played_in_tournament} games played
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Not Responded — selectable with warning */}
              {notRespondedRsvps.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-orange-700 mb-2 flex items-center gap-2">
                    <CircleDashed className="h-4 w-4" />
                    Not Responded ({notRespondedRsvps.length})
                    <span className="text-xs font-normal text-slate-500 ml-1">— Registered but haven't RSVP'd. Selecting will notify parents.</span>
                  </h4>
                  <div className="space-y-1">
                    {notRespondedRsvps.map(rsvp => (
                      <div
                        key={rsvp.player_id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedPlayerIds.includes(rsvp.player_id)
                            ? 'bg-orange-50 border-orange-300'
                            : 'bg-white border-orange-200 hover:bg-orange-50'
                        }`}
                        onClick={() => togglePlayerSelection(rsvp.player_id)}
                        data-testid={`not-responded-row-${rsvp.player_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedPlayerIds.includes(rsvp.player_id)}
                            onCheckedChange={() => togglePlayerSelection(rsvp.player_id)}
                            data-testid={`select-player-${rsvp.player_id}`}
                          />
                          <div>
                            <p className="font-medium">{rsvp.player_name}</p>
                            <p className="text-xs text-orange-600">No RSVP yet</p>
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className={`${
                            rsvp.games_played_in_tournament === 0
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {rsvp.games_played_in_tournament} games played
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tentative RSVPs — selectable to nudge for firmer answer */}
              {tentativeRsvps.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-yellow-700 mb-2 flex items-center gap-2">
                    <HelpCircle className="h-4 w-4" />
                    Tentative ({tentativeRsvps.length})
                    <span className="text-xs font-normal text-slate-500 ml-1">— Responded "Maybe". Selecting will nudge for a firm Yes/No.</span>
                  </h4>
                  <div className="space-y-1">
                    {tentativeRsvps.map(rsvp => (
                      <div
                        key={rsvp.player_id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedPlayerIds.includes(rsvp.player_id)
                            ? 'bg-yellow-100 border-yellow-400'
                            : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
                        }`}
                        onClick={() => togglePlayerSelection(rsvp.player_id)}
                        data-testid={`tentative-row-${rsvp.player_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedPlayerIds.includes(rsvp.player_id)}
                            onCheckedChange={() => togglePlayerSelection(rsvp.player_id)}
                            data-testid={`select-tentative-${rsvp.player_id}`}
                          />
                          <HelpCircle className="h-4 w-4 text-yellow-600" />
                          <div>
                            <p className="font-medium text-slate-700">{rsvp.player_name}</p>
                            <p className="text-xs text-yellow-700">Answered "Maybe"</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                          {rsvp.games_played_in_tournament} games
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No RSVPs */}
              {noRsvps.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                    <XCircle className="h-4 w-4" />
                    Unavailable ({noRsvps.length})
                  </h4>
                  <div className="space-y-1">
                    {noRsvps.map(rsvp => {
                      const stillSelected = selectedPlayerIds.includes(rsvp.player_id);
                      return (
                        <div
                          key={rsvp.player_id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            stillSelected
                              ? 'border-amber-400 bg-amber-50'
                              : 'border-red-200 bg-red-50 opacity-50'
                          }`}
                          data-testid={`unavailable-row-${rsvp.player_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <p className={`font-medium ${stillSelected ? 'text-amber-900' : 'text-slate-500'}`}>
                              {rsvp.player_name}
                            </p>
                            {stillSelected && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs" title="This player is still in the selected roster despite being marked Unavailable. They will still count toward games-played unless removed.">
                                Still selected
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                              {rsvp.games_played_in_tournament} games
                            </Badge>
                            {stillSelected && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedPlayerIds(prev => prev.filter(id => id !== rsvp.player_id))}
                                className="h-7 text-xs text-amber-700 hover:bg-amber-100"
                                data-testid={`remove-from-selection-${rsvp.player_id}`}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={onSendRsvpReminder}
            disabled={sendingNudge || selectedNudgeableCount === 0}
            className="border-amber-400 text-amber-700 hover:bg-amber-50"
            data-testid="send-rsvp-reminder-btn"
            title={selectedNudgeableCount === 0
              ? 'Check one or more players in "Not Responded" or "Tentative" to nudge'
              : `Send email + WhatsApp + in-app nudge to ${selectedNudgeableCount} parent(s)`}
          >
            {sendingNudge ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
            ) : (
              <><Bell className="h-4 w-4 mr-2" /> Send RSVP Reminder ({selectedNudgeableCount})</>
            )}
          </Button>
          <Button
            onClick={onSaveSelectedPlayers}
            disabled={submitting}
            data-testid="save-selected-players"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              `Save Selection (${selectedPlayerIds.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelectPlayersDialog;
